import { CardBrand, PaymentType } from '@afterpay/types'
import { lastDayOfMonth, isValid, parse, differenceInMonths } from 'date-fns'
import { CardNumberValidity, CardExpiryValidity } from './types'
import { PaymentIconNameMapping, PaymentIconNames } from '@afterpaytouch/core'
import { isNullOrUndefined } from '@afterpay/utils'

const CARDS_REGEXP = [
  { name: CardBrand.VISA, pattern: /^4/ },
  { name: CardBrand.MASTERCARD, pattern: /^5[1-5]/ },
  {
    name: CardBrand.MASTERCARD,
    pattern: /^2(?:2(?:2[1-9]|[3-9]\d)|[3-6]\d\d|7(?:[01]\d|20))/,
  },
  { name: CardBrand.MASTERCARD, pattern: /^601576/ }, // bin 601576 is also categorised as MASTERCARD (PAYL-1786)
  // { name: 'DINERS_CLUB', pattern: /^3(0|[68])/ },
  { name: CardBrand.AMEX, pattern: /^3[4,7]/ },
  // { name: 'DISCOVER', pattern: /^6([45]|01)/ }
  // {name: "JCB", pattern: /^35/},
]

const checkLuhn = (creditCardNumber: string): boolean => {
  // The Luhn Algorithm
  if (/[^0-9\s]+/.test(creditCardNumber)) {
    return false
  }
  let cDigit
  let nCheck = 0
  let nDigit = 0
  let bEven = false
  let n
  let value = creditCardNumber
  value = value.replace(/\D/g, '')
  for (n = value.length - 1; n >= 0; n--) {
    cDigit = value.charAt(n)
    nDigit = parseInt(cDigit, 10)
    if (bEven) {
      nDigit *= 2
      if (nDigit > 9) {
        nDigit -= 9
      }
    }
    nCheck += nDigit
    bEven = !bEven
  }
  return nCheck % 10 === 0
}

export const getCardBrandFromCardNumber = (cardNumber: string): CardBrand | undefined => {
  const digits = cardNumber.replace(/\s/g, '')
  return CARDS_REGEXP.find(({ pattern }) => pattern.test(digits))?.name
}

// Expected format is 'mm/yy'
const convertExpiryDateFromString = (expiryString: string): Date | null => {
  const expiryDate = parse(expiryString, 'MM/yy', new Date())

  if (!isValid(expiryDate)) {
    return null
  }

  return lastDayOfMonth(expiryDate)
}

export const validateCVV = (cvv: string, cardBrand: CardBrand | undefined): boolean => {
  if (/^\d+$/.test(cvv) && cvv.length === (cardBrand === CardBrand.AMEX ? 4 : 3)) {
    return true
  }
  return false
}

const hasValidLength = (cardBrand?: CardBrand, cardNumber?: string): boolean => {
  if (!cardNumber || !cardBrand) {
    return false
  }

  const digits = cardNumber.replace(/\s/g, '')

  switch (cardBrand) {
    case CardBrand.AMEX:
      return digits.length === 15
    case CardBrand.VISA:
      return digits.length === 13 || digits.length === 16 || digits.length === 19
    case CardBrand.MASTERCARD:
      return digits.length === 16
    // case CardBrand.DINERS_CLUB:
    //   return digits.length === 14
    // case CardBrand.DISCOVER:
    //   return digits.length === 16
    default:
      return false
  }
}

export const cardNumberValidator =
  (acceptableCardBrands: CardBrand[], blockedBins: string[] = []) =>
  (cardNumber: string): CardNumberValidity => {
    const binIsBlocked = blockedBins.some((bin) => cardNumber?.startsWith(bin))
    const cardBrand = getCardBrandFromCardNumber(cardNumber ?? '')
    const cardNumberValid = checkLuhn(cardNumber) && hasValidLength(cardBrand, cardNumber)
    const selectedBrandIsInvalid = !acceptableCardBrands?.includes(cardBrand as CardBrand)

    if (!cardNumberValid) {
      return CardNumberValidity.InvalidNumber
    }

    if (selectedBrandIsInvalid) {
      return CardNumberValidity.CardBrandIneligible
    }

    if (binIsBlocked) {
      return CardNumberValidity.BinIneligible
    }

    return CardNumberValidity.Valid
  }

export const cardExpiryValidator =
  (lastPaymentDate?: Date | null, cardExpiryLimitInMonths: number = 0, paymentType?: PaymentType) =>
  (expiry: string): CardExpiryValidity => {
    const expiryDate = convertExpiryDateFromString(expiry)
    const today = new Date()

    if (expiryDate === null) {
      return CardExpiryValidity.Invalid
    }

    if (expiryDate < today) {
      return CardExpiryValidity.Expired
    }

    if (paymentType === PaymentType.PCL) {
      const monthsBetweenDates = differenceInMonths(expiryDate, today)
      if (monthsBetweenDates <= cardExpiryLimitInMonths) {
        return CardExpiryValidity.ExpireInLimitMonths
      }
    }

    if (lastPaymentDate !== null && lastPaymentDate !== undefined && expiryDate < lastPaymentDate) {
      return CardExpiryValidity.ExpiringSoon
    }

    return CardExpiryValidity.Valid
  }

export const validateCardHolderName = (cardHolderName?: string): boolean => cardHolderName?.trim().length !== 0

const DEFAULT_ALLOWED_CARD_BRAND = [CardBrand.MASTERCARD, CardBrand.VISA]

export const getCardIconFromCardBrand = (
  enabledCardBrands: CardBrand[] = DEFAULT_ALLOWED_CARD_BRAND,
  selectedCardBrand?: CardBrand
): PaymentIconNames | undefined => {
  if (isNullOrUndefined(selectedCardBrand)) {
    return
  }
  if (enabledCardBrands.includes(selectedCardBrand)) {
    return PaymentIconNameMapping[selectedCardBrand]
  }
  return undefined
}
